import React, { useRef } from "react";
import { Box, BoxProps } from "@chakra-ui/react";
import { useWebGlShader } from "./useWebGlShader";

// Sunlight through a canopy: two layers of drifting value noise, thresholded into soft patches,
// plus faint diagonal beams. Rendered as an additive overlay on the hero photograph.
const DAPPLED_LIGHT_FRAGMENT = `
precision mediump float;
uniform float u_time;
uniform vec2 u_resolution;
uniform vec2 u_mouse;
varying vec2 v_uv;

float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123); }
float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}
float fbm(vec2 p) {
  float v = 0.0;
  float amp = 0.5;
  for (int i = 0; i < 4; i++) {
    v += amp * noise(p);
    p = p * 2.03 + vec2(1.7, 9.2);
    amp *= 0.5;
  }
  return v;
}

void main() {
  vec2 uv = v_uv;
  float aspect = u_resolution.x / u_resolution.y;
  vec2 p = vec2(uv.x * aspect, uv.y);
  float t = u_time * 0.08;

  // Leaves moving overhead: slow drift in two directions.
  float leaves = fbm(p * 2.6 + vec2(t, -t * 0.6));
  float leaves2 = fbm(p * 5.2 - vec2(t * 0.7, t * 0.4));
  // Thresholded high so the patches stay small and scattered rather than pooling into fog.
  float patch = smoothstep(0.6, 0.78, leaves * 0.65 + leaves2 * 0.35);

  // Beams from the upper right, gently swaying with the mouse.
  vec2 beamDir = normalize(vec2(-0.55 + (u_mouse.x - 0.5) * 0.2, 1.0));
  float along = dot(p - vec2(aspect * 0.8, 1.1), vec2(beamDir.y, -beamDir.x));
  float beams = smoothstep(0.35, 1.0, noise(vec2(along * 6.0 + t * 2.0, 0.0))) * smoothstep(0.1, 0.9, uv.y);

  // Fade the light toward the bottom-left so the moss stays dark where the type sits.
  float falloff = smoothstep(-0.2, 1.0, uv.x * 0.6 + uv.y * 0.8);
  float light = (patch * 0.28 + beams * 0.42) * falloff;

  vec3 sun = vec3(1.0, 0.93, 0.72);
  gl_FragColor = vec4(sun * light, light * 0.9);
}`;

// The same canopy, seen as shadow on a pale wall: where the light shader adds sun, this one
// darkens toward leaf-green. Meant for a multiply blend over a light background.
const LEAF_SHADOW_FRAGMENT = DAPPLED_LIGHT_FRAGMENT.replace(
  "gl_FragColor = vec4(sun * light, light * 0.9);",
  "vec3 ink = vec3(0.36, 0.52, 0.30); float shade = (1.0 - light) * 0.5; gl_FragColor = vec4(mix(vec3(1.0), ink, shade), 1.0);",
);

export function DappledLight({ animate = true, mode = "light", ...props }: BoxProps & { animate?: boolean; mode?: "light" | "shadow" }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useWebGlShader(canvasRef, { fragmentShader: mode === "shadow" ? LEAF_SHADOW_FRAGMENT : DAPPLED_LIGHT_FRAGMENT, animate, pixelRatio: 1 });
  return <Box as="canvas" ref={canvasRef} position="absolute" inset={0} w="100%" h="100%" pointerEvents="none" mixBlendMode={mode === "shadow" ? "multiply" : "screen"} {...props} />;
}
